import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import React, { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useProfile } from "src/auth/Auth";
import PermissionProvider from "src/context/PermissionContext";
import { useSnackbar } from "src/context/ShowMessage";
import "./App.css";
import { ApiResponse } from "./apis";
import { GetCountries } from "./apis/loopupListApis";
import { getUserPermissions } from "./apis/userApis";
import { useAuth } from "./auth/AuthContext";
import { FieldRefsProvider } from "./context/FieldRefsContext";
import i18n from "./i18n";
import { useAppDispatch } from "./redux/hooks";
import {
  getAuthState,
  getAuthStatus,
  setPermissions,
} from "./redux/slices/authSlice";
import { fetchClientFeatures } from "./redux/slices/featuresSlice";
import { setCountries } from "./redux/slices/lookupListSlice";
import { getSystemState } from "./redux/slices/systemSlice";
import Routes from "./routes";
import ChartStyle from "./shared_components/chart/ChartStyle";
import darkTheme from "./theme/dark/darkTheme";
import lightTheme from "./theme/light/lightTheme";

const App: React.FC = () => {
  const isAuthenticated = useSelector(getAuthStatus);
  const { permissions } = useSelector(getAuthState);
  const { clientId } = useProfile();
  const { displayMessage } = useSnackbar();
  const navigate = useNavigate();

  const { theme: currentTheme } = useSelector(getSystemState);

  const dispatch = useAppDispatch();

  const applicationTheme = createTheme(
    JSON.parse(
      JSON.stringify(currentTheme === "light" ? lightTheme : darkTheme)
    )
  );

  const auth = useAuth();

  const loadLookupList = async () => {
    await GetCountries().then((response) => {
      if (response.status === 200) {
        dispatch(setCountries({ countries: response.data }));
      }
    });
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response: ApiResponse = await getUserPermissions();

        if (response.status === 200) {
          dispatch(await setPermissions({ permissions: response.data }));
        }
      } catch (error: any) {
        displayMessage(`${error.message}`, "error");
      }
    };
    if (isAuthenticated) {
      dispatch(fetchClientFeatures());
      fetchPermissions && fetchPermissions();

      clientId && auth.fetchLogo(clientId);
      clientId && auth.fetchClientCountry(Number(clientId));
    }
    loadLookupList();
  }, [isAuthenticated]);

  //#region logout
  function signOutUser() {
    navigate("/login", { replace: true });
    auth.signOut();
  }

  const onIdle = () => {
    signOutUser();
  };

  useIdleTimer({
    timeout: 4* 60 * 60 * 1000,
    onIdle,
  });
  //#endregion logout

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={applicationTheme}>
        <CssBaseline />
        <PermissionProvider permissions={permissions}>
          <FieldRefsProvider>
            <ChartStyle />
            <div className="App">
              <Routes />
            </div>
          </FieldRefsProvider>
        </PermissionProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
};

export default App;
